/**
 * Google Maps API Configuration
 * 
 * Manages Google Maps API key and configuration for the analytics dashboard.
 * The API key should be set as an environment variable in Rails and passed
 * to the frontend components through props or data attributes.
 */

// Google Maps API configuration
export interface GoogleMapsConfig {
  apiKey: string;
  libraries: string[];
  version: string;
  language: string;
  region: string;
}

// Default configuration for Google Maps
export const DEFAULT_GOOGLE_MAPS_CONFIG: Omit<GoogleMapsConfig, 'apiKey'> = {
  libraries: ['geometry', 'drawing', 'places'],
  version: '3.35',
  language: 'en',
  region: 'US'
};

/**
 * Get Google Maps API key from various sources
 * Priority: props > meta tag > environment variable
 */
export const getGoogleMapsApiKey = (providedApiKey?: string): string => {
  // 1. Use provided API key (highest priority)
  if (providedApiKey) {
    return providedApiKey;
  }

  // 2. Check meta tag in document head
  const metaTag = document.querySelector('meta[name="google-maps-api-key"]');
  if (metaTag) {
    const apiKey = metaTag.getAttribute('content');
    if (apiKey) {
      return apiKey;
    }
  }

  // 3. Check data attribute on body or html element
  const bodyApiKey = document.body.dataset.googleMapsApiKey;
  if (bodyApiKey) {
    return bodyApiKey;
  }

  // 4. Check if it's available globally (set by Rails)
  if ((window as any).GOOGLE_MAPS_API_KEY) {
    return (window as any).GOOGLE_MAPS_API_KEY;
  }

  // Log error if no API key found
  console.error('Google Maps API key not found. Please set it via:', [
    '1. Pass as prop to GoogleMapView component',
    '2. Add <meta name="google-maps-api-key" content="YOUR_KEY"> to document head',
    '3. Set data-google-maps-api-key attribute on body element',
    '4. Set window.GOOGLE_MAPS_API_KEY global variable'
  ].join('\n'));

  return '';
};

/**
 * Create complete Google Maps configuration
 */
export const createGoogleMapsConfig = (
  apiKey?: string,
  overrides?: Partial<GoogleMapsConfig>
): GoogleMapsConfig => {
  return {
    apiKey: getGoogleMapsApiKey(apiKey),
    ...DEFAULT_GOOGLE_MAPS_CONFIG,
    ...overrides
  };
};

/**
 * Validate Google Maps API key format
 */
export const validateGoogleMapsApiKey = (apiKey: string): boolean => {
  if (!apiKey) return false;
  
  // Google Maps API keys typically start with "AIza" and are 39 characters long
  const apiKeyPattern = /^AIza[0-9A-Za-z-_]{35}$/;
  return apiKeyPattern.test(apiKey);
};

/**
 * Get required Google Maps API permissions for this application
 */
export const getRequiredGoogleMapsApis = (): string[] => {
  return [
    'Maps JavaScript API',
    'Geocoding API', // For potential address lookups
    'Places API' // For potential location search features
  ];
};

/**
 * Instructions for setting up Google Maps API key in Rails
 */
export const getRailsSetupInstructions = (): string => {
  return `
To set up Google Maps API key in Rails:

1. Add to config/credentials.yml.enc:
   google:
     maps_api_key: YOUR_API_KEY_HERE

2. In your controller or application helper:
   @google_maps_api_key = Rails.application.credentials.google[:maps_api_key]

3. In your view template:
   <%= content_for :head do %>
     <meta name="google-maps-api-key" content="<%= @google_maps_api_key %>">
   <% end %>

   OR

   <body data-google-maps-api-key="<%= @google_maps_api_key %>">

4. Required API permissions:
   ${getRequiredGoogleMapsApis().map(api => `   - ${api}`).join('\n')}

5. Ensure your API key is restricted to your domain for security.
`;
};

export default {
  getGoogleMapsApiKey,
  createGoogleMapsConfig,
  validateGoogleMapsApiKey,
  getRequiredGoogleMapsApis,
  getRailsSetupInstructions,
  DEFAULT_GOOGLE_MAPS_CONFIG
};